import * as Yup from 'yup';

/** Length limits for text fields (min/max characters) */
const LEN = {
  customerName: { min: 1, max: 100 },
  orderNumber: { min: 1, max: 50 },
  modeOfParcel: { max: 50 },
  courierName: { max: 100 },
  trackingNumber: { max: 100 },
  ringSize: { max: 100 },
  samaanGinti: { max: 100 },
};

const MAX_PRODUCT_IMAGES = 5;

/** Transform dayjs (or similar) to Date for Yup.date() */
const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v);

const stringRequired = (field, min, max) =>
  Yup.string()
    .required(`${field} is required`)
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .min(min, `${field} must be at least ${min} character(s)`)
    .max(max, `${field} must be at most ${max} characters`);

const stringOptional = (max, label) =>
  Yup.string()
    .nullable()
    .optional()
    .transform((v) => (v === '' || v == null ? null : (typeof v === 'string' ? v.trim() : v)))
    .max(max, `${label} must be at most ${max} characters`);

export const createOrderSchema = Yup.object().shape({
  customerName: stringRequired(
    'Customer name',
    LEN.customerName.min,
    LEN.customerName.max
  ),
  orderNumber: stringRequired(
    'Order number',
    LEN.orderNumber.min,
    LEN.orderNumber.max
  ),
  orderDate: Yup.date()
    .required('Order date is required')
    .transform(toDate),
  targetDate: Yup.date()
    .required('Target date is required')
    .transform(toDate)
    .when('orderDate', ([orderDate], schema) => {
      const d = orderDate && typeof orderDate.toDate === 'function' ? orderDate.toDate() : orderDate;
      if (!d) return schema;
      return schema.min(d, 'Target date cannot be before order date');
    }),
  sourceOfOrder: Yup.string().required('Please select source of order'),
  modeOfParcel: stringOptional(LEN.modeOfParcel.max, 'Mode of parcel'),
});

export const editOrderSchema = createOrderSchema.shape({
  parcelImages: Yup.array()
    .max(MAX_PRODUCT_IMAGES, `Maximum ${MAX_PRODUCT_IMAGES} parcel images allowed`),
  status: Yup.string().nullable(),
  envelopeReady: Yup.boolean().nullable(),
  orderReady: Yup.string().nullable(),
  stonePrepared: Yup.boolean().nullable(),
  courierName: stringOptional(LEN.courierName.max, 'Courier name'),
  trackingNumber: stringOptional(LEN.trackingNumber.max, 'Tracking number'),
  qualityGhatTheek: Yup.boolean().nullable(),
  qualityStoneSahi: Yup.boolean().nullable(),
  qualityOilLaga: Yup.boolean().nullable(),
  qualitySettingTheek: Yup.boolean().nullable(),
  qualityNoScratch: Yup.boolean().nullable(),
  qualityPolishTheek: Yup.boolean().nullable(),
  qualityRhodiumTheek: Yup.boolean().nullable(),
  qualityEarringPush: Yup.boolean().nullable(),
  qualityLockBand: Yup.boolean().nullable(),
  ringSize: stringOptional(LEN.ringSize.max, 'Ring size'),
  samaanGinti: stringOptional(LEN.samaanGinti.max, 'समान की गिनती'),
  marking: Yup.array().nullable(),
});

/** Run schema and return field errors for Ant Design Form.setFields(). */
export async function validateOrderForm(schema, values) {
  try {
    await schema.validate(values, { abortEarly: false });
    return null;
  } catch (err) {
    if (err.name !== 'ValidationError') throw err;
    return err.inner.map((e) => ({
      name: e.path,
      errors: [e.message],
    }));
  }
}
